import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { BLOG_T } from "@/lib/blogTranslations";
import { getPostBySlug, BLOG_POSTS, CATEGORIES } from "@/lib/blogPosts";
import { BRAND } from "@/lib/config";
import { track, registerSuperProps } from "@/lib/analytics";
import LanguageSwitcher from "@/components/landing/LanguageSwitcher";
import LeadMagnetCard from "@/components/landing/LeadMagnetCard";
import { MagazineBanner, VerbosBanner } from "@/components/landing/MagazineBanner";
import { BLOG_AUDIO } from "@/lib/blogAudio";
import { formatBlogDate } from "@/lib/formatDate";
import { useCanonical } from "@/lib/useCanonical";
import { AUTHOR, personRef } from "@/lib/authorSchema";
import { ArrowRight, ArrowLeft, Construction, X, Volume2, Repeat } from "lucide-react";

const formatDate = (iso, lang) => formatBlogDate(iso, lang);

const PostAudioContext = createContext(null);
const GUILLEMETS_RE = /«\s*([^»]+?)\s*»/g;

const Spoken = ({ text }) => {
  const ctx = useContext(PostAudioContext);
  if (!ctx || !text.includes("«")) return text;
  const out = [];
  let last = 0;
  for (const m of text.matchAll(GUILLEMETS_RE)) {
    const end = m.index + m[0].length;
    out.push(text.slice(last, end));
    const s = audioSlug(m[1]);
    if (ctx.set.has(s)) {
      out.push(
        <PlayButton key={m.index} src={`${ctx.dir}/${s}.mp3`} label={m[1]} extra="ml-1.5 align-middle" testId={`inline-audio-${s}-${m.index}`} />
      );
    }
    last = end;
  }
  out.push(text.slice(last));
  return out;
};

const INLINE_RE = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*/g;

const Inline = ({ text }) => {
  if (typeof text !== "string") return text;
  const out = [];
  let last = 0;
  for (const m of text.matchAll(INLINE_RE)) {
    if (m.index > last) out.push(<Spoken key={`t${last}`} text={text.slice(last, m.index)} />);
    const k = m.index;
    if (m[1]) {
      const external = !m[2].startsWith("https://npdlbr.com.br");
      out.push(
        <a
          key={k}
          href={m[2]}
          {...(external ? { target: "_blank", rel: "noopener" } : {})}
          className="text-[#d9381e] link-underline"
        >
          {m[1]}
        </a>
      );
    } else if (m[3]) out.push(<strong key={k}><Spoken text={m[3]} /></strong>);
    else out.push(<em key={k}>{m[4]}</em>);
    last = k + m[0].length;
  }
  if (last < text.length) out.push(<Spoken key={`t${last}`} text={text.slice(last)} />);
  return out;
};

const TABLE_HINT = {
  pt: "Deslize para ver os exemplos",
  en: "Swipe to see the examples",
  fr: "Faites glisser pour voir les exemples",
};

export const audioSlug = (word) =>
  word.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const PlayButton = ({ src, label, extra = "mr-2", testId }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    if (!ref.current) {
      ref.current = new Audio(src);
      ref.current.onended = () => setPlaying(false);
    }
    if (playing) {
      ref.current.pause();
      ref.current.currentTime = 0;
      setPlaying(false);
    } else {
      ref.current.play();
      setPlaying(true);
    }
  };
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={`Ouvir: ${label}`}
      data-testid={testId || `table-audio-${audioSlug(label)}`}
      className={`${extra} inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors ${playing ? "bg-[#d9381e] border-[#d9381e] text-white" : "border-[#e5e3db] text-[#d9381e] hover:bg-[#f0efea]"}`}
    >
      <Volume2 className="h-3.5 w-3.5" />
    </button>
  );
};

const TableBlock = ({ block }) => {
  const { lang } = useLanguage();
  const wide = block.head.length > 2;
  const audioSrc = (i, row) => {
    if (i === 0 && block.audio) return `${block.audioDir}/${audioSlug(row[0])}.mp3`;
    if (i === 2 && block.audioExamples) return `${block.audioDir}/${audioSlug(row[0])}-exemple.mp3`;
    return null;
  };
  return (
    <div className="mt-6">
      {wide && (
        <p className="sm:hidden mb-2 flex items-center justify-end gap-1.5 text-xs uppercase tracking-[0.14em] text-[#5c5c5c]" data-testid="blog-table-swipe-hint">
          {TABLE_HINT[lang] || TABLE_HINT.pt} <ArrowRight className="h-3.5 w-3.5 animate-pulse" />
        </p>
      )}
      <div className="relative">
        <div className="overflow-x-auto rounded-xl border border-[#e5e3db] bg-white" data-testid="blog-table-block">
          <table className={`w-full ${wide ? "min-w-[560px]" : ""} text-left text-sm sm:text-base text-[#3a3a3a]`}>
            <thead className="bg-[#f0efea] text-[#1e2a5a]">
              <tr>
                {block.head.map((h) => (
                  <th key={h} scope="col" className="px-4 py-3 font-display font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e5e3db]">
              {block.rows.map((row) => (
                <tr key={row[0]}>
                  {row.map((cell, i) => (
                    <td key={`${row[0]}-${i}`} className={`px-4 py-2.5 align-top ${i === 0 ? "font-medium text-[#1e2a5a]" : ""}`}>
                      {audioSrc(i, row) ? (
                        <span className="inline-flex items-center">
                          <PlayButton src={audioSrc(i, row)} label={cell} />
                          <Inline text={cell} />
                        </span>
                      ) : (
                        <Inline text={cell} />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {wide && (
          <div className="sm:hidden pointer-events-none absolute inset-y-px right-px w-10 rounded-r-xl bg-gradient-to-l from-white to-transparent" />
        )}
      </div>
    </div>
  );
};

const QUIZ_COPY = {
  pt: { show: "Ver resposta", hide: "Esconder resposta", label: "Resposta", right: "Acertei", wrong: "Errei",
        score: (x, n) => `Você acertou ${x} de ${n}.`, cta: "Descubra seu nível com o teste grátis de 5 minutos" },
  en: { show: "Show answer", hide: "Hide answer", label: "Answer", right: "Got it right", wrong: "Got it wrong",
        score: (x, n) => `You got ${x} out of ${n}.`, cta: "Find your level with the free 5-minute test" },
  fr: { show: "Voir la réponse", hide: "Masquer la réponse", label: "Réponse", right: "J'ai bon", wrong: "J'ai faux",
        score: (x, n) => `Vous avez ${x} bonne(s) réponse(s) sur ${n}.`, cta: "Découvrez votre niveau avec le test gratuit de 5 minutes" },
};

const ScoreChoice = ({ n, value, onPick, copy }) => (
  <div className="mt-3 flex gap-2" data-testid={`quiz-self-check-${n}`}>
    {[[true, copy.right], [false, copy.wrong]].map(([ok, label]) => (
      <button
        key={label}
        type="button"
        onClick={() => onPick(ok)}
        aria-pressed={value === ok}
        data-testid={`quiz-${ok ? "right" : "wrong"}-${n}`}
        className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
          value === ok
            ? ok ? "border-[#1e2a5a] bg-[#1e2a5a] text-white" : "border-[#d9381e] bg-[#d9381e] text-white"
            : "border-[#e5e3db] text-[#3a3a3a] hover:bg-[#f0efea]"
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

const QuizItem = ({ item, n, copy, result, onPick }) => {
  const [open, setOpen] = useState(false);
  return (
    <li className="rounded-xl border border-[#e5e3db] bg-white p-5" data-testid={`quiz-item-${n}`}>
      <p className="text-base sm:text-lg leading-relaxed text-[#1e2a5a] font-medium">
        <span className="mr-2 font-display text-[#d9381e]">{n}.</span>
        <Inline text={item.q} />
      </p>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`quiz-toggle-${n}`}
        className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#1e2a5a] px-4 py-1.5 text-sm text-[#1e2a5a] transition-colors hover:bg-[#1e2a5a] hover:text-white"
      >
        {open ? copy.hide : copy.show}
      </button>
      <div className={open ? "" : "hidden"}>
        <p className="mt-3 text-base sm:text-lg leading-relaxed text-[#3a3a3a]" data-testid={`quiz-answer-${n}`}>
          <span className="font-medium text-[#d9381e]">{copy.label}:</span> <Inline text={item.a} />
        </p>
        <ScoreChoice n={n} value={result} onPick={onPick} copy={copy} />
      </div>
    </li>
  );
};

const QuizBlock = ({ block }) => {
  const { lang } = useLanguage();
  const copy = QUIZ_COPY[lang] || QUIZ_COPY.pt;
  const [results, setResults] = useState({});
  const total = block.items.length;
  const done = Object.keys(results).length === total;
  const right = Object.values(results).filter(Boolean).length;
  return (
    <div className="mt-6">
      <ol className="space-y-4" data-testid="blog-quiz-block">
        {block.items.map((item, i) => (
          <QuizItem
            key={item.q}
            item={item}
            n={i + 1}
            copy={copy}
            result={results[i]}
            onPick={(ok) => setResults((r) => ({ ...r, [i]: ok }))}
          />
        ))}
      </ol>
      {done && (
        <div className="mt-5 rounded-xl bg-[#1e2a5a] p-5 text-white" data-testid="quiz-score">
          <p className="font-display text-xl">{copy.score(right, total)}</p>
          <Link
            to="/teste-de-nivel"
            data-testid="quiz-score-level-test-link"
            className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#e5b3b3] link-underline"
          >
            {copy.cta} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  );
};

const lineSrc = (block, i) => `${block.audioDir}/dialogo-${i + 1}-${audioSlug(block.lines[i].who)}.mp3`;

const useRepeatMode = (srcFor) => {
  const audio = useRef(null);
  const [step, setStep] = useState(null);
  const play = (i) => {
    audio.current?.pause();
    audio.current = new Audio(srcFor(i));
    audio.current.onended = () => setStep({ i, phase: "yours" });
    audio.current.play();
    setStep({ i, phase: "playing" });
  };
  const stop = () => {
    audio.current?.pause();
    setStep(null);
  };
  useEffect(() => () => audio.current?.pause(), []);
  useEffect(() => {
    document.body.classList.toggle("repeat-mode-active", !!step);
    return () => document.body.classList.remove("repeat-mode-active");
  }, [step]);
  return { step, play, stop };
};

const RepeatPanel = ({ step, total, play, stop, noun = "fala" }) => {
  const last = step.i === total - 1;
  const btn = "rounded-full px-4 py-1.5 text-sm transition-colors";
  return (
    <div className="sticky bottom-3 z-30 mt-4 flex flex-wrap items-center gap-2 rounded-xl bg-[#1e2a5a] p-4 text-white shadow-lg" data-testid="repeat-panel">
      <p className="mr-auto text-sm" data-testid="repeat-status">
        {step.phase === "playing" ? `Ouça a ${noun} ${step.i + 1} de ${total}…` : "Sua vez: repita em voz alta."}
      </p>
      {step.phase === "yours" && (
        <>
          <button type="button" onClick={() => play(step.i)} data-testid="repeat-again" className={`${btn} border border-white/40 hover:bg-white/10`}>
            Ouvir de novo
          </button>
          <button type="button" onClick={() => play(last ? 0 : step.i + 1)} data-testid="repeat-next" className={`${btn} bg-[#d9381e] hover:bg-[#b82f18]`}>
            {last ? "Recomeçar" : `Próxima ${noun}`}
          </button>
        </>
      )}
      <button type="button" onClick={stop} data-testid="repeat-exit" className={`${btn} text-white/70 hover:text-white`}>
        Sair
      </button>
    </div>
  );
};

const DialogueBlock = ({ block }) => {
  const { step, play, stop } = useRepeatMode((i) => lineSrc(block, i));
  return (
    <div className="mt-6 rounded-xl border border-[#e5e3db] bg-[#f0efea] p-5 sm:p-6 space-y-2.5" data-testid="blog-dialogue-block">
      {block.audio && (
        <div className="mb-3 flex flex-wrap items-center gap-y-2 text-sm text-[#5c5c5c]">
          <PlayButton src={block.audio} label="dialogo" />
          <span className="mr-4">Ouça o diálogo</span>
          {block.lineAudio && !step && (
            <button
              type="button"
              onClick={() => play(0)}
              data-testid="repeat-mode-start"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#1e2a5a] px-3 py-1 text-sm text-[#1e2a5a] transition-colors hover:bg-[#1e2a5a] hover:text-white"
            >
              <Repeat className="h-3.5 w-3.5" /> Modo repetição
            </button>
          )}
        </div>
      )}
      {block.lines.map((l, i) => (
        <p
          key={`${l.who}-${l.text}`}
          className={`flex items-start rounded-lg text-base sm:text-lg leading-relaxed text-[#3a3a3a] transition-colors ${step?.i === i ? "bg-white -mx-2 px-2 py-1" : ""}`}
          data-testid={`dialogue-line-${i + 1}`}
        >
          {block.lineAudio && (
            <span className="mt-0.5">
              <PlayButton src={lineSrc(block, i)} label={`fala-${i + 1}`} />
            </span>
          )}
          <span>
            <span className="font-display font-medium text-[#d9381e]">{l.who} :</span>{" "}
            <span lang="fr">{l.text}</span>
          </span>
        </p>
      ))}
      {step && <RepeatPanel step={step} total={block.lines.length} play={play} stop={stop} />}
    </div>
  );
};

const RepeatPhrasesBlock = ({ block }) => {
  const ctx = useContext(PostAudioContext);
  const { step, play, stop } = useRepeatMode((i) => `${ctx?.dir}/${audioSlug(block.items[i])}.mp3`);
  useEffect(() => {
    if (step) document.querySelector(`[data-testid="repeat-phrase-${step.i + 1}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [step?.i]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!ctx) return null;
  return (
    <div className="mt-8 rounded-xl border border-[#e5e3db] bg-[#f0efea] p-5 sm:p-6" data-testid="blog-repeat-phrases-block">
      <h3 className="font-display text-xl text-[#1e2a5a]">{block.title}</h3>
      <p className="mt-1 text-sm text-[#5c5c5c]">{block.intro}</p>
      {!step && (
        <button
          type="button"
          onClick={() => play(0)}
          data-testid="repeat-phrases-start"
          className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#1e2a5a] px-4 py-2 text-sm text-white transition-colors hover:bg-[#2c3b78]"
        >
          <Repeat className="h-4 w-4" /> {block.cta}
        </button>
      )}
      <ol className="mt-4 space-y-1.5">
        {block.items.map((item, i) => (
          <li
            key={item}
            data-testid={`repeat-phrase-${i + 1}`}
            className={`rounded-lg px-2 py-1 text-base text-[#3a3a3a] transition-colors ${step?.i === i ? "bg-white font-medium text-[#1e2a5a]" : ""}`}
          >
            <span className="mr-2 font-display text-[#d9381e]">{i + 1}.</span>
            <span lang="fr">{item}</span>
          </li>
        ))}
      </ol>
      {step && <RepeatPanel step={step} total={block.items.length} play={play} stop={stop} noun="frase" />}
    </div>
  );
};

const Block = ({ block }) => {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="post-heading font-display mt-12 mb-3 text-2xl sm:text-3xl tracking-tight text-[#1e2a5a]">
          <Spoken text={block.text} />
        </h2>
      );
    case "h3":
      return (
        <h3 className="font-display mt-8 mb-2 text-xl text-[#1e2a5a]">
          <Spoken text={block.text} />
        </h3>
      );
    case "p":
      return (
        <p className="mt-4 text-base sm:text-lg leading-[1.75] text-[#3a3a3a]">
          <Inline text={block.text} />
        </p>
      );
    case "ol":
      return (
        <ol className="mt-4 list-decimal pl-6 space-y-2 text-base sm:text-lg leading-[1.7] text-[#3a3a3a] marker:text-[#d9381e]">
          {block.text.map((item) => (
            <li key={item}><Inline text={item} /></li>
          ))}
        </ol>
      );
    case "table":
      return <TableBlock block={block} />;
    case "dialogue":
      return <DialogueBlock block={block} />;
    case "quiz":
      return <QuizBlock block={block} />;
    case "repeat":
      return <RepeatPhrasesBlock block={block} />;
    case "ul":
      return (
        <ul className="mt-4 space-y-2 text-base sm:text-lg leading-[1.7] text-[#3a3a3a]">
          {block.text.map((item, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-3 h-1 w-1 shrink-0 rounded-full bg-[#d9381e]" />
              <span><Inline text={item} /></span>
            </li>
          ))}
        </ul>
      );
    case "quote":
      return (
        <blockquote className="mt-8 pl-6 border-l-2 border-[#d9381e] italic font-display text-lg text-[#1e2a5a]">
          <Inline text={block.text} />
        </blockquote>
      );
    case "todo":
      return (
        <li className="flex gap-3 mt-3 text-[#5c5c5c]">
          <Construction className="h-4 w-4 mt-1 text-[#d9381e] shrink-0" />
          <span className="text-base leading-relaxed">{block.text}</span>
        </li>
      );
    case "audio":
      return (
        <figure
          className="mt-8 rounded-xl border border-[#e5e3db] bg-[#f0efea] p-5"
          data-testid="blog-audio-block"
        >
          <figcaption className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#d9381e]">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#d9381e]" />
            {block.caption || "Áudio · Prof Lionel"}
          </figcaption>
          <audio
            controls
            preload="none"
            src={block.src}
            className="w-full"
            data-testid="blog-audio-player"
          >
            Seu navegador não suporta áudio HTML5.
          </audio>
          {block.transcript && (
            <details className="mt-3 text-sm text-[#5c5c5c]">
              <summary className="cursor-pointer hover:text-[#1e2a5a]">
                Ver transcrição
              </summary>
              <p className="mt-2 leading-relaxed">{block.transcript}</p>
            </details>
          )}
        </figure>
      );
    default:
      return null;
  }
};

export default function BlogPost() {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const t = BLOG_T[lang] || BLOG_T.pt;

  const post = getPostBySlug(slug);
  useCanonical(post ? `/blog/${slug}` : "/blog");

  // Floating CTA: appears after reader passes 60% of the article and the inline CTA
  // is NOT yet on screen. Dismissible per session.
  const articleRef = useRef(null);
  const inlineCtaRef = useRef(null);
  const [floatingVisible, setFloatingVisible] = useState(false);
  const [floatingDismissed, setFloatingDismissed] = useState(false);
  const [inlineCtaVisible, setInlineCtaVisible] = useState(false);

  // PostHog impression dedupe — fire once per (placement, slug, lang).
  const inlineSeenRef = useRef(false);
  const floatingSeenRef = useRef(false);

  useEffect(() => {
    if (!post) return undefined;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const node = articleRef.current;
        if (!node) return;
        const rect = node.getBoundingClientRect();
        const viewportH = window.innerHeight || 0;
        // progress within the article: 0 when top hits viewport bottom, 1 when bottom passes top
        const total = rect.height + viewportH;
        const passed = viewportH - rect.top;
        const progress = total > 0 ? Math.min(Math.max(passed / total, 0), 1) : 0;
        setFloatingVisible(progress >= 0.6);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [post, slug]);

  useEffect(() => {
    if (!post) return undefined;
    const node = inlineCtaRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => setInlineCtaVisible(entry.isIntersecting),
      { threshold: 0.2 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [post, slug]);

  // Reset dismissal & impression dedupe when slug/lang changes
  useEffect(() => {
    setFloatingDismissed(false);
    inlineSeenRef.current = false;
    floatingSeenRef.current = false;
  }, [slug, lang]);

  // Fire impression events the first time each CTA becomes visible
  useEffect(() => {
    if (inlineCtaVisible && !inlineSeenRef.current) {
      inlineSeenRef.current = true;
      track("blog_cta_view", { placement: "inline", slug, lang });
    }
  }, [inlineCtaVisible, slug, lang]);

  useEffect(() => {
    if (floatingVisible && !floatingDismissed && !inlineCtaVisible && !floatingSeenRef.current) {
      floatingSeenRef.current = true;
      track("blog_cta_view", { placement: "floating", slug, lang });
    }
  }, [floatingVisible, floatingDismissed, inlineCtaVisible, slug, lang]);

  // Inject per-post SEO metadata: BreadcrumbList + Open Graph + Twitter Card + BlogPosting JSON-LD
  useEffect(() => {
    if (!post) return undefined;
    const i18nPost = post.i18n[lang] || post.i18n.fr || post.i18n.pt;
    const blogLabel =
      lang === "en"
        ? "Blog · French articles"
        : lang === "fr"
        ? "Blog · articles de français"
        : "Blog · artigos de francês";
    const url = `https://npdlbr.com.br/blog/${slug}`;
    const fullTitle = i18nPost.seoTitle || `${i18nPost.title} · Blog NPDL`;
    const description = i18nPost.metaDescription || i18nPost.excerpt || `Artigo do blog do Prof. Lionel · ${i18nPost.title}`;
    const ogLocale =
      lang === "en" ? "en_US" : lang === "fr" ? "fr_FR" : "pt_BR";

    // Helper: set or create a <meta> tag (and mark it dynamic so we clean it up)
    const setMeta = (selector, attr, value, content) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, value);
        el.dataset.dynamic = "blog-meta";
        document.head.appendChild(el);
      } else {
        // Remember original content so we can restore on unmount
        if (!el.dataset.original) el.dataset.original = el.getAttribute("content") || "";
      }
      el.setAttribute("content", content);
      return { el, created };
    };

    const handles = [];
    handles.push(setMeta('meta[property="og:title"]', "property", "og:title", i18nPost.title));
    handles.push(setMeta('meta[property="og:description"]', "property", "og:description", description));
    handles.push(setMeta('meta[property="og:url"]', "property", "og:url", url));
    handles.push(setMeta('meta[property="og:image"]', "property", "og:image", post.coverImage));
    handles.push(setMeta('meta[property="og:image:alt"]', "property", "og:image:alt", i18nPost.title));
    handles.push(setMeta('meta[property="og:type"]', "property", "og:type", "article"));
    handles.push(setMeta('meta[property="og:locale"]', "property", "og:locale", ogLocale));
    handles.push(setMeta('meta[name="twitter:title"]', "name", "twitter:title", i18nPost.title));
    handles.push(setMeta('meta[name="twitter:description"]', "name", "twitter:description", description));
    handles.push(setMeta('meta[name="twitter:image"]', "name", "twitter:image", post.coverImage));
    handles.push(setMeta('meta[name="twitter:image:alt"]', "name", "twitter:image:alt", i18nPost.title));
    handles.push(setMeta('meta[name="description"]', "name", "description", description));
    if (post.draft) handles.push(setMeta('meta[name="robots"]', "name", "robots", "noindex, nofollow"));

    // BreadcrumbList JSON-LD
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Início", item: "https://npdlbr.com.br/" },
        { "@type": "ListItem", position: 2, name: blogLabel, item: "https://npdlbr.com.br/blog" },
        { "@type": "ListItem", position: 3, name: i18nPost.title, item: url },
      ],
    };

    // BlogPosting JSON-LD — unlocks Google Discover + Top Stories card.
    // Uses the shared Person entity (via @id) so Google AI Overview can resolve
    // author authority across every post + the dedicated /prof-lionel profile.
    const blogPosting = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: i18nPost.title,
      description,
      image: [post.coverImage],
      datePublished: post.date,
      dateModified: post.date,
      author: personRef(),
      publisher: {
        "@type": "Organization",
        name: "Na Ponta Da Língua Brasil",
        logo: {
          "@type": "ImageObject",
          url: "https://customer-assets.emergentagent.com/job_french-for-all-1/artifacts/bv8f2u2u_Capture%20d%E2%80%99e%CC%81cran%202026-06-19%20a%CC%80%2016.45.23.png",
        },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      inLanguage: lang,
      articleSection: CATEGORIES[post.category]?.[lang] || post.category,
      // Speakable: tells Google Assistant / AI Overview / voice which parts of
      // the article are safe to read aloud (the TL;DR + every H2 heading).
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: [".post-tldr", ".post-heading"],
      },
    };

    // Optional FAQPage schema — only emitted when the post explicitly ships
    // curated Q&A. Google surfaces these directly in the SERP and in AI
    // Overview extractive answers.
    const faqItems = Array.isArray(post.faq?.[lang])
      ? post.faq[lang]
      : Array.isArray(post.faq?.pt)
      ? post.faq.pt
      : null;
    const faqPage =
      faqItems && faqItems.length
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }
        : null;

    const breadcrumbEl = document.createElement("script");
    breadcrumbEl.type = "application/ld+json";
    breadcrumbEl.dataset.dynamic = "breadcrumb";
    breadcrumbEl.textContent = JSON.stringify(breadcrumb);
    document.head.appendChild(breadcrumbEl);

    const postingEl = document.createElement("script");
    postingEl.type = "application/ld+json";
    postingEl.dataset.dynamic = "blogposting";
    postingEl.textContent = JSON.stringify(blogPosting);
    document.head.appendChild(postingEl);

    let faqEl = null;
    if (faqPage) {
      faqEl = document.createElement("script");
      faqEl.type = "application/ld+json";
      faqEl.dataset.dynamic = "faqpage";
      faqEl.textContent = JSON.stringify(faqPage);
      document.head.appendChild(faqEl);
    }

    const prevTitle = document.title;
    document.title = fullTitle;

    return () => {
      breadcrumbEl.remove();
      postingEl.remove();
      if (faqEl) faqEl.remove();
      // Restore original meta values (or remove if we created them)
      handles.forEach(({ el, created }) => {
        if (created) {
          el.remove();
        } else if (el.dataset.original !== undefined) {
          el.setAttribute("content", el.dataset.original);
          delete el.dataset.original;
        }
      });
      document.title = prevTitle;
    };
  }, [post, slug, lang]);

  if (!post) return <Navigate to="/blog" replace />;

  const i18n = post.i18n[lang] || post.i18n.fr || post.i18n.pt;
  const cat = CATEGORIES[post.category]?.[lang] || post.category;

  const others = BLOG_POSTS.filter((p) => p.slug !== slug && !p.draft).slice(0, 2);
  const tldrText =
    (post.tldr && (post.tldr[lang] || post.tldr.pt)) || i18n.excerpt;
  const faqItems =
    (Array.isArray(post.faq?.[lang]) && post.faq[lang]) ||
    (Array.isArray(post.faq?.pt) && post.faq.pt) ||
    null;
  const faqHeading =
    lang === "fr"
      ? "Questions fréquentes"
      : lang === "en"
      ? "Frequently asked questions"
      : "Perguntas frequentes";
  const tldrLabel =
    lang === "fr"
      ? "Réponse rapide"
      : lang === "en"
      ? "Quick answer"
      : "Resposta rápida";
  const aboutAuthorLabel =
    lang === "fr"
      ? "À propos de l'auteur"
      : lang === "en"
      ? "About the author"
      : "Sobre o autor";

  const showFloating = floatingVisible && !floatingDismissed && !inlineCtaVisible;

  // Group consecutive todos into a single styled list
  const renderedContent = [];
  let todoGroup = [];
  i18n.content.forEach((block, idx) => {
    if (block.type === "todo") {
      todoGroup.push(block);
    } else {
      if (todoGroup.length) {
        renderedContent.push(
          <ul
            key={`todo-${idx}`}
            className="mt-6 bg-[#f0efea] border border-[#e5e3db] rounded-xl p-6 space-y-1 list-none"
          >
            {todoGroup.map((b, i) => (
              <Block key={i} block={b} />
            ))}
          </ul>
        );
        todoGroup = [];
      }
      renderedContent.push(<Block key={idx} block={block} />);
    }
  });
  if (todoGroup.length) {
    renderedContent.push(
      <ul
        key="todo-final"
        className="mt-6 bg-[#f0efea] border border-[#e5e3db] rounded-xl p-6 space-y-1 list-none"
      >
        {todoGroup.map((b, i) => (
          <Block key={i} block={b} />
        ))}
      </ul>
    );
  }

  return (
    <div data-testid="blog-post-page" className="min-h-screen bg-[#f4f1ea] text-[#1e2a5a]">
      <header className="border-b border-[#e5e3db]">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#1e2a5a] text-[#1e2a5a] font-display italic">
              L
            </span>
            <span className="font-display text-sm sm:text-base">{BRAND.name}</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/blog" className="text-xs sm:text-sm link-underline">
              {t.post.backToBlog}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-12 sm:py-20">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm text-[#5c5c5c] hover:text-[#1e2a5a] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> {t.post.backToBlog.replace("← ", "")}
        </Link>

        <div className="mt-8 flex items-center gap-3 text-xs uppercase tracking-[0.18em]">
          <span className="text-[#d9381e] font-medium">{cat}</span>
          <span className="text-[#5c5c5c]">·</span>
          <span className="text-[#5c5c5c]">{formatDate(post.date, lang)}</span>
          <span className="text-[#5c5c5c]">·</span>
          <span className="text-[#5c5c5c]">
            {post.readTime} {t.list?.minRead || "min"}
          </span>
        </div>

        <h1 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          {i18n.title}
        </h1>

        {!post.plain && (
          <p className="mt-6 text-lg sm:text-xl text-[#5c5c5c] leading-relaxed">
            {i18n.excerpt}
          </p>
        )}

        {/* TL;DR / Quick Answer — 40-80 word summary optimised for Google
            AI Overview & voice extraction. The .post-tldr class is referenced
            by the SpeakableSpecification schema above. */}
        {post.tldr && (
        <aside
          className="post-tldr mt-8 rounded-xl border-l-4 border-[#d9381e] bg-[#f0efea] px-5 py-4 sm:px-6 sm:py-5"
          data-testid="blog-post-tldr"
          aria-label={tldrLabel}
        >
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.22em] text-[#d9381e] font-medium">
            {tldrLabel}
          </p>
          <p className="mt-2 text-base sm:text-lg leading-relaxed text-[#1e2a5a]">
            {tldrText}
          </p>
        </aside>
        )}

        <div className="mt-10 overflow-hidden border border-[#e5e3db] rounded-xl bg-[#f0efea] aspect-[16/9]">
          <img
            src={post.coverImage}
            alt={i18n.title}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        </div>

        {post.isSkeleton && (
          <p className="mt-8 text-xs uppercase tracking-[0.18em] text-[#d9381e] inline-flex items-center gap-2 bg-[#d9381e]/10 px-3 py-1.5 rounded-full">
            <Construction className="h-3.5 w-3.5" /> {t.post.skeletonNote}
          </p>
        )}

        <PostAudioContext.Provider
          value={BLOG_AUDIO[post.slug] ? { dir: `/audio/blog/${post.slug}`, set: new Set(BLOG_AUDIO[post.slug]) } : null}
        >
          <article ref={articleRef} className="mt-4">{renderedContent}</article>
        </PostAudioContext.Provider>
        {!JSON.stringify(i18n.content).includes("solteseusverbos") && (
          <VerbosBanner lang={lang} testId="blog-post-verbos-banner" />
        )}
        {!post.isSkeleton && !JSON.stringify(i18n.content).includes("solteseufrances") && (
          <MagazineBanner lang={lang} testId="blog-post-magazine-banner" />
        )}

        {/* Lead-magnet card — placed before the existing CTA */}
        <LeadMagnetCard context="blog_post" />

        {/* Visible FAQ — mirrors the FAQPage JSON-LD above (only rendered when
            the post ships a curated Q&A array). This is what Google AI Overview
            and voice search extract as a direct answer. */}
        {faqItems && faqItems.length > 0 && (
          <section
            className="mt-16"
            data-testid="blog-post-faq"
            aria-labelledby="blog-post-faq-heading"
          >
            <h2
              id="blog-post-faq-heading"
              className="post-heading font-display text-2xl sm:text-3xl tracking-tight"
            >
              {faqHeading}
            </h2>
            <div className="mt-6 divide-y divide-[#e5e3db] border-t border-b border-[#e5e3db]">
              {faqItems.map((f) => (
                <details key={f.q} className="group py-4">
                  <summary className="flex cursor-pointer items-start justify-between gap-4 font-display text-lg text-[#1e2a5a] hover:text-[#d9381e] transition-colors">
                    <span>{f.q}</span>
                    <span className="mt-1 text-2xl leading-none text-[#d9381e] group-open:rotate-45 transition-transform">
                      +
                    </span>
                  </summary>
                  <p className="mt-3 pr-8 text-base leading-relaxed text-[#3a3a3a]">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Author card — reinforces E-E-A-T on every post and drives clicks
            to the /prof-lionel profile page (which carries the full Person
            schema, sameAs links, and credentials). */}
        <section
          className="mt-16 rounded-2xl border border-[#e5e3db] bg-white p-6 sm:p-8 flex gap-5 items-start"
          data-testid="blog-post-author-card"
        >
          <img
            src={AUTHOR.image}
            alt={`${AUTHOR.name} — ${AUTHOR.jobTitle}`}
            width="80"
            height="80"
            loading="lazy"
            decoding="async"
            className="w-20 h-20 rounded-full object-cover shrink-0 border border-[#e5e3db]"
          />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#d9381e]">
              {aboutAuthorLabel}
            </p>
            <Link
              to="/prof-lionel"
              className="mt-1 inline-block font-display text-xl sm:text-2xl text-[#1e2a5a] hover:text-[#d9381e] transition-colors"
              data-testid="blog-post-author-link"
              onClick={() =>
                track("blog_author_click", { placement: "author_card", slug, lang })
              }
            >
              {AUTHOR.name}
            </Link>
            <p className="mt-1 text-sm text-[#5c5c5c]">{AUTHOR.jobTitle}</p>
            <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#3a3a3a]">
              {AUTHOR.description}
            </p>
          </div>
        </section>

        {/* CTA — Conversion block at end of post */}
        <aside
          ref={inlineCtaRef}
          data-testid="blog-post-cta"
          className="mt-20 relative overflow-hidden rounded-2xl bg-[#1e2a5a] text-[#f4f1ea] p-8 sm:p-12"
        >
          {/* subtle accent corner */}
          <div
            aria-hidden="true"
            className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#d9381e]/30 blur-3xl"
          />
          <div className="relative">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#f4f1ea]/30 px-3 py-1 text-[10px] sm:text-xs uppercase tracking-[0.22em] text-[#f4f1ea]/80">
              <span className="h-1.5 w-1.5 rounded-full bg-[#d9381e]" />
              {t.post.ctaBadge}
            </span>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight">
              {t.post.ctaTitle}
            </h2>
            <p className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed text-[#f4f1ea]/85">
              {t.post.ctaBody}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/#contact"
                data-testid="blog-post-cta-book"
                onClick={() => {
                  registerSuperProps({ last_cta_source: "blog_inline" });
                  track("blog_cta_click", { action: "book", placement: "inline", slug, lang });
                }}
                className="group inline-flex items-center gap-2 rounded-full bg-[#f4f1ea] px-6 py-3 text-sm font-medium text-[#1e2a5a] transition-colors hover:bg-[#d9381e] hover:text-[#f4f1ea]"
              >
                {t.post.ctaBook}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/teste-de-nivel"
                data-testid="blog-post-cta-test"
                onClick={() => {
                  registerSuperProps({ last_cta_source: "blog_inline" });
                  track("blog_cta_click", { action: "test", placement: "inline", slug, lang });
                }}
                className="inline-flex items-center gap-2 rounded-full border border-[#f4f1ea]/40 px-6 py-3 text-sm font-medium text-[#f4f1ea] transition-colors hover:border-[#f4f1ea] hover:bg-[#f4f1ea]/10"
              >
                {t.post.ctaTest}
              </Link>
            </div>
            <p className="mt-6 text-xs uppercase tracking-[0.18em] text-[#f4f1ea]/55">
              {t.post.ctaMicro}
            </p>
          </div>
        </aside>

        {/* Other posts */}
        {others.length > 0 && (
          <section className="mt-20">
            <h3 className="font-display text-2xl sm:text-3xl tracking-tight">
              {t.post.readNext}
            </h3>
            <ul className="mt-8 grid sm:grid-cols-2 gap-8">
              {others.map((o) => {
                const oi = o.i18n[lang] || o.i18n.fr || o.i18n.pt;
                return (
                  <li key={o.slug}>
                    <Link
                      to={`/blog/${o.slug}`}
                      data-testid={`related-${o.slug}`}
                      className="group flex flex-col"
                    >
                      <div className="overflow-hidden border border-[#e5e3db] rounded-xl aspect-[4/3]">
                        <img
                          src={o.coverImage}
                          alt={oi.title}
                          loading="lazy"
                          decoding="async"
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <h4 className="font-display mt-4 text-xl">{oi.title}</h4>
                      <p className="text-sm text-[#5c5c5c] mt-2">{oi.excerpt}</p>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </main>

      {/* Floating CTA — appears after 60% read and hides when inline CTA is on screen */}
      <div
        data-testid="blog-post-floating-cta"
        aria-hidden={!showFloating}
        data-floating="true"
        className={`fixed z-40 bottom-5 sm:bottom-8 left-1/2 -translate-x-1/2 sm:left-auto sm:right-8 sm:translate-x-0 transition-all duration-500 ${
          showFloating
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-4 pointer-events-none"
        }`}
      >
        <div className="flex items-center gap-1 rounded-full bg-[#1e2a5a] text-[#f4f1ea] shadow-2xl shadow-[#1e2a5a]/30 ring-1 ring-[#f4f1ea]/10">
          <Link
            to="/#contact"
            data-testid="blog-post-floating-cta-book"
            onClick={() => {
              registerSuperProps({ last_cta_source: "blog_floating" });
              track("blog_cta_click", { action: "book", placement: "floating", slug, lang });
            }}
            className="group inline-flex items-center gap-2.5 pl-5 pr-3 py-3 sm:pl-6 sm:pr-4 sm:py-3.5 text-sm font-medium rounded-full hover:bg-[#d9381e] transition-colors"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#d9381e] opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#d9381e]" />
            </span>
            {t.post.ctaFloating}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <button
            type="button"
            onClick={() => {
              setFloatingDismissed(true);
              track("blog_cta_floating_dismiss", { slug, lang });
            }}
            aria-label={t.post.ctaFloatingClose}
            data-testid="blog-post-floating-cta-close"
            className="pr-3 pl-1 py-3 sm:pr-4 sm:pl-2 sm:py-3.5 text-[#f4f1ea]/60 hover:text-[#f4f1ea] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
